import Link from 'next/link'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import compose from 'recompose/compose'
import ICONS from 'components/common/icon/const'
import checkoutPaymentMethodDuck from 'components/checkout/payment-options/duck'
import ModalNoReturn from 'components/modal/no-return'
import useModal from 'components/portal/useModal'
import { Modal } from 'components/portal/modal'
import { FEATURE_TOGGLER } from 'constants/global'
import Typo from 'constants/typography'
import { customFlagValue } from 'lib/utils/handle-flag/const'
import { withI18next } from 'lib/i18n/withI18next'
import { getAuthStatus } from 'lib/utils/common/commonUtils'
import { covertPropertiesToCamelCase } from 'lib/utils/common/object'
import { hasLocizeTranslation, locizeTranslation } from 'lib/utils/locize'
import { PAYMENT_FETCH_TYPES } from 'lib/utils/payments/paymentMethodUtils'
import { useFlagsmithGetFlagValue } from 'lib/flagsmith'
import usePomeloCashbackEnable from 'pages/user/pomelo-credit/credit-cashback/usePomeloCashbackEnable'
import { getShopInfo } from 'lib/utils/shop-lang'
import {
  PDP_USP_MESSAGE,
  GUEST,
  MEMBER,
  NON_MEMBER,
  JOIN_CTA,
  SIGN_UP_CTA,
} from './const'
import HoolahInfoModal from './hoolah-info-modal'
import PomeloPickUpModal from './pomelo-pickup-modal'
import styles from './styles.scss'
import USP from './usp'
import copy from '../../outfit-shopping/copy'
import DeliverySummary from '../../mobile-buying-flow/DeliverySummary'
import policyCopy from '../../delivery-policy/copy'

const findLoyaltyType = ({ authStatus, t }) => {
  const isLoggedIn = authStatus.isFetched && !authStatus.isGuest
  const isLoyaltySignedUp = authStatus.isFetched && authStatus.isLoyaltyMember
  const loyaltyMember = isLoyaltySignedUp ? MEMBER : NON_MEMBER
  const usp = isLoggedIn ? loyaltyMember : GUEST

  const [ctaLabel, title, description] = locizeTranslation(t, {
    ...(isLoggedIn ? JOIN_CTA : SIGN_UP_CTA),
    ...usp,
  })
  return { title, description, ctaLabel, joinCta: usp.joinCta }
}

const isHoolahAvailable = (paymentMethodList) => {
  let hoolahStatus
  if (paymentMethodList?.payment_methods) {
    const [hoolahPayment] = paymentMethodList.payment_methods.filter(
      (method) => method.type === 'hoolah',
    )
    hoolahStatus = !!hoolahPayment
  }
  return hoolahStatus
}

const ProductUSP = ({
  auth,
  fetchPaymentOptions,
  internationalization: { country, language, shop },
  isPaymentOptionsFetching,
  isPhone,
  paymentOptions,
  product,
  resetPaymentOptions,
  t,
  policyFirst,
  compact,
  deliveryPolicyShown,
}) => {
  const [isPickUpOpen, setIsPickUpOpen] = useModal(false)
  const [isHoolahOpen, setIsHoolahOpen] = useModal(false)
  const [showModalNoReturn, setShowModalNoReturn] = useModal(false)
  const isCashbackEnable = usePomeloCashbackEnable(shop)
  const isTTBEnable = useFlagsmithGetFlagValue('TTBVisibility_20230228', false)
  const hoolahFlagValue = customFlagValue({ defaultValue: false }) // TODO: handle with flagsmith ('InstallmentsPreCheckout-20220627' is old flag name)

  const currentShop = getShopInfo({ key: 'id_shop', value: shop })

  const { isAllowLoyalty, isEnablePickUp, isFreeReturnable } =
    covertPropertiesToCamelCase(currentShop)

  useEffect(() => {
    resetPaymentOptions()
    fetchPaymentOptions({ type: PAYMENT_FETCH_TYPES.GET_PAYMENT_METHODS })
  }, [fetchPaymentOptions, resetPaymentOptions])

  const authStatus = getAuthStatus(auth)
  const returnable = product?.is_returnable
  const isItalist = product?.is_italist ?? false
  const [
    pickupTitle,
    pickupDescription,
    pickupCta,
    hoolahTitle,
    hoolahDescription,
    hoolahCta,
    deliverTitle,
    deliverDescription,
    returnTitle,
    noReturnTitle,
    noReturnSubtitle,
    noReturnCta,
    PDP_USP_ITALIST_LUXURY_GOODS,
    PDP_USP_ITALIST_LUXURY_GOODS_SUBTITLE,
    // PDP_USP_ITALIST_LUXURY_GOODS_CTA,
    PDP_USP_ITALIST_FREE_SHIPPING_TITLE,
    PDP_USP_ITALIST_FREE_SHIPPING_SUBTITLE,
    PDP_USP_ITALIST_FULFILLS_TITLE,
    PDP_USP_ITALIST_FULFILLS_SUBTITLE,
    PDP_USP_ITALIST_FULFILLS_CTA,
  ] = locizeTranslation(t, PDP_USP_MESSAGE)
  const uspLoyalty = findLoyaltyType({ authStatus, t })
  const isHoolahOn = isHoolahAvailable(paymentOptions)

  const uspData = [
    {
      key: 'luxury-goods',
      icon: ICONS.diamond,
      usp: {
        title: PDP_USP_ITALIST_LUXURY_GOODS,
        description: PDP_USP_ITALIST_LUXURY_GOODS_SUBTITLE,
      },
      hidden: !isItalist,
      dataCy: 'italist_luxury_good_pdp_usp',
    },
    {
      key: 'italist_free_shipping',
      icon: ICONS.deliveryTruck,
      usp: {
        title: PDP_USP_ITALIST_FREE_SHIPPING_TITLE,
        description: PDP_USP_ITALIST_FREE_SHIPPING_SUBTITLE,
      },
      hidden: !isItalist,
      dataCy: 'italist_free_shipping_pdp_usp',
    },
    {
      key: 'italist_fulfills',
      icon: ICONS.deliveryBox,
      usp: {
        title: PDP_USP_ITALIST_FULFILLS_TITLE,
        description: PDP_USP_ITALIST_FULFILLS_SUBTITLE,
      },
      children: (
        <Link href="/info/faq" as={`/${country}/${language}/info/italist`}>
          <a className="pdp__usp-link">
            <span className={Typo.overline}>
              {PDP_USP_ITALIST_FULFILLS_CTA}
            </span>
          </a>
        </Link>
      ),
      hidden: !isItalist,
      dataCy: 'italist_fulfills_pdp_usp',
    },
    {
      key: 'deliver',
      icon: ICONS.deliveryUSP,
      usp: {
        title: policyFirst ? uspLoyalty.title : deliverTitle,
        description: policyFirst ? uspLoyalty.description : deliverDescription,
      },
      dataCy: 'free_delivery_pdp_usp',
      isFetching: !authStatus.isFetched,
      children:
        !FEATURE_TOGGLER.loyalty ||
        !isCashbackEnable ||
        !isAllowLoyalty ? undefined : (
          <Link
            href="/loyalty-program"
            as={`/${country}/${language}/loyalty-program`}
          >
            <a className="pdp__usp-link">
              <span className={Typo.overline}>{uspLoyalty.ctaLabel}</span>
            </a>
          </Link>
        ),
      hidden:
        isItalist ||
        (policyFirst &&
          (!FEATURE_TOGGLER.loyalty || !isCashbackEnable || !isAllowLoyalty)),
    },
    {
      key: 'pickup',
      icon: ICONS.hanger,
      usp: {
        title: pickupTitle,
        description: pickupDescription,
        cta: pickupCta,
      },
      children: (
        <Modal isOpen={isPickUpOpen} disabledPointerEvents>
          <PomeloPickUpModal
            onClose={() => setIsPickUpOpen(false)}
            isPhone={isPhone}
          />
        </Modal>
      ),
      onClickOpenModal: setIsPickUpOpen,
      hidden: !isTTBEnable || !isEnablePickUp || isItalist,
      dataCy: 'ttb_pdp_usp',
    },
    {
      key: 'hoolah',
      icon: ICONS.creditCard,
      usp: {
        title: hoolahTitle,
        description: hoolahDescription,
        cta: hoolahCta,
      },
      children: (
        <Modal isOpen={isHoolahOpen} disabledPointerEvents>
          <HoolahInfoModal onClose={() => setIsHoolahOpen(false)} />
        </Modal>
      ),
      isFetching: isPaymentOptionsFetching,
      onClickOpenModal: setIsHoolahOpen,
      hidden: !hoolahFlagValue || !isHoolahOn || isItalist,
      dataCy: 'hoolah_pdp_usp',
    },
    {
      key: 'free-return',
      icon: ICONS.returnsUSP,
      usp: { title: policyFirst ? copy(t, language, 'eligible') : returnTitle },
      hidden: !isFreeReturnable || !returnable || isItalist,
      dataCy: 'free_return_pdp_usp',
    },
    {
      key: 'no-return',
      icon: ICONS.noReturn,
      usp: {
        title: noReturnTitle,
        description: noReturnSubtitle,
        cta: noReturnCta,
      },
      hidden:
        returnable ||
        isItalist ||
        (policyFirst && typeof returnable !== 'boolean'),
      children: (
        <ModalNoReturn
          showCta
          open={showModalNoReturn}
          onBackdropClick={() => setShowModalNoReturn(false)}
        />
      ),
      onClickOpenModal: setShowModalNoReturn,
      dataCy: 'no_return_pdp_usp',
    },
  ]

  const policyKeys = [
    'italist_free_shipping',
    'italist_fulfills',
    'free-return',
    'no-return',
  ]
  const visibleUspData = uspData.filter(
    (data) =>
      data.hidden !== true &&
      !(deliveryPolicyShown && policyKeys.includes(data.key)),
  )

  const renderUsps = (items) =>
    items.map(({ key, children, loyalty, usp, ...rest }, index) => (
      <USP
        key={key}
        usp={usp}
        {...rest}
        isShowDivider={index < items.length - 1}
      >
        {children}
      </USP>
    ))

  if (compact) {
    const pickup = visibleUspData.filter(({ key }) => key === 'pickup')
    const otherServices = visibleUspData.filter(({ key }) => key !== 'pickup')
    return (
      <div className="pdp-mobile-service-disclosures">
        <style jsx>{styles}</style>
        {(!deliveryPolicyShown || !!otherServices.length) && (
          <details className="pdp-mobile-service-disclosure">
            <summary className={Typo.subtitle2}>
              {deliveryPolicyShown
                ? policyCopy(t, 'additionalServices', language)
                : hasLocizeTranslation(
                    t,
                    'PDP_ACCORDION_SHIPPING_RETURNS',
                    'Shipping & Returns',
                  )}
            </summary>
            <div className="pdp__usp-container">
              {!deliveryPolicyShown && (
                <DeliverySummary product={product || {}} />
              )}
              {renderUsps(otherServices)}
            </div>
          </details>
        )}
        {!!pickup.length && (
          <details className="pdp-mobile-service-disclosure">
            <summary className={Typo.subtitle2}>{pickupTitle}</summary>
            <div className="pdp__usp-container">{renderUsps(pickup)}</div>
          </details>
        )}
      </div>
    )
  }

  return (
    <div className="pdp__usp-container">
      <style jsx>{styles}</style>
      {policyFirst && !deliveryPolicyShown && (
        <DeliverySummary product={product || {}} />
      )}
      {visibleUspData.map(({ key, children, loyalty, usp, ...rest }, index) => {
        const isLastItem = visibleUspData.length - 1 === index
        return (
          <USP key={key} usp={usp} {...rest} isShowDivider={!isLastItem}>
            {children}
          </USP>
        )
      })}
    </div>
  )
}

ProductUSP.propTypes = {
  compact: PropTypes.bool,
  deliveryPolicyShown: PropTypes.bool,
  policyFirst: PropTypes.bool,
  auth: PropTypes.shape({
    customerData: PropTypes.shape({
      isFetched: PropTypes.bool.isRequired,
      loyalty_signup: PropTypes.number,
    }),
    user: PropTypes.shape({}),
  }).isRequired,
  fetchPaymentOptions: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({
    country: PropTypes.string,
    language: PropTypes.string,
    shop: PropTypes.number.isRequired,
  }).isRequired,
  isPaymentOptionsFetching: PropTypes.bool,
  isPhone: PropTypes.bool.isRequired,
  paymentOptions: PropTypes.shape({}),
  product: PropTypes.shape({
    is_italist: PropTypes.bool,
    is_returnable: PropTypes.bool,
    cashback: PropTypes.shape({ cashback_earned: PropTypes.number }),
  }),
  resetPaymentOptions: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}

ProductUSP.defaultProps = {
  compact: false,
  deliveryPolicyShown: false,
  policyFirst: false,
  product: undefined,
  paymentOptions: undefined,
  isPaymentOptionsFetching: true,
}

export default compose(
  connect(
    (state) => ({
      auth: state.auth,
      isPhone: state.device.isPhone,
      product: state.product.payload,
      internationalization: state.internationalization,
      paymentOptions: state?.checkoutPaymentMethod?.payload,
      isPaymentOptionsFetching: state?.checkoutPaymentMethod?.isFetching,
    }),
    (dispatch) =>
      bindActionCreators(
        {
          fetchPaymentOptions: checkoutPaymentMethodDuck.creators.get,
          resetPaymentOptions: checkoutPaymentMethodDuck.creators.invalidate,
        },
        dispatch,
      ),
  ),
  withI18next(),
)(ProductUSP)
